import { useEffect, useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import parse from 'html-react-parser';
import { X } from 'lucide-react';
import { marked } from 'marked';
import { Link } from 'react-router-dom';

import { Icon } from './icon';
import { APIBaseChronos } from '../../api/hosts';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/new/components/shadcn/sidebar';
import { useAnnouncement } from '@/new/hooks/useAnnouncement';
import { announcements, type Announcement } from '@/new/lib/announcements';

function getAnnouncementImageUrl(image?: string) {
  return image ? `${APIBaseChronos}/api/static/release-notes-images/${image}` : undefined;
}

function CollapsedHelpButton() {
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const recent = announcements.slice(0, 3);

  return (
    <>
      <SidebarGroup onClick={(e) => e.stopPropagation()}>
        <SidebarGroupContent>
          <SidebarMenu className="text-gray-600">
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Open announcements"
                    className="flex size-4.75 cursor-pointer items-center justify-center rounded-full border border-gray-400/50 bg-white text-[10px] text-gray-700"
                  >
                    ?
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent side="top" align="start" className="w-64">
                  <DropdownMenuLabel>Recent Features</DropdownMenuLabel>
                  {recent.map((a) => (
                    <DropdownMenuItem
                      key={a.id}
                      onSelect={() => setSelectedAnnouncement(a)}
                      className="flex items-center justify-between"
                    >
                      <p>{a.heading}</p>
                      <Icon className="lucide--arrow-up-right text-gray-400" />
                    </DropdownMenuItem>
                  ))}

                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Support</DropdownMenuLabel>

                  <DropdownMenuItem asChild>
                    <Link to="/help-centre">
                      <Icon className="lucide--book-open" /> Help Centre
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/release-notes">
                      <Icon className="lucide--notebook" />
                      Release Notes
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* Modal for the announcement selected from the dropdown */}
      {selectedAnnouncement && (
        <AnnouncementModal
          announcement={selectedAnnouncement}
          open={!!selectedAnnouncement}
          onOpenChange={(open) => {
            if (!open) setSelectedAnnouncement(null);
          }}
        />
      )}
    </>
  );
}

function AnnouncementModal({
  announcement,
  open,
  onOpenChange,
  onDismiss,
}: {
  announcement: Announcement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDismiss?: () => void;
}) {
  const modalContent = useMemo(() => {
    if (!announcement.modalMarkdown) return null;

    const html = marked.parse(announcement.modalMarkdown, { async: false }) as string;
    return parse(html);
  }, [announcement.modalMarkdown]);
  const hasModalBody = Boolean(announcement.dialogImage || modalContent);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium text-gray-700">
            {announcement.modalHeading ?? announcement.heading}
          </DialogTitle>
        </DialogHeader>
        {hasModalBody && (
          <DialogDescription asChild className="text-sm text-gray-600">
            <div>
              {announcement.dialogImage && (
                <img
                  crossOrigin="anonymous"
                  className="mx-auto mt-8 mb-10 h-52 rounded-lg object-cover"
                  src={getAnnouncementImageUrl(announcement.dialogImage)}
                  alt=""
                />
              )}
              {modalContent && (
                <div className="prose prose-sm prose-h2:text-base prose-headings:font-medium prose-p:leading-relaxed prose-li:leading-relaxed max-w-none px-4 text-gray-600">
                  {modalContent}
                </div>
              )}
            </div>
          </DialogDescription>
        )}
        <DialogFooter className="mt-6 gap-1">
          {announcement.learnMoreHref && (
            <Button variant="ghost" asChild>
              <Link to={announcement.learnMoreHref} onClick={() => onOpenChange(false)}>
                Learn more
              </Link>
            </Button>
          )}
          <Button
            onClick={() => {
              onOpenChange(false);
              onDismiss?.();
            }}
          >
            {onDismiss ? 'Got it' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SidebarAnnouncement() {
  const { announcement, dismiss } = useAnnouncement();
  const { state } = useSidebar();
  const [modalOpen, setModalOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    setDismissing(false);
  }, [announcement?.id]);

  const isCollapsed = state === 'collapsed';
  const showCard = announcement && !dismissing && !isCollapsed;

  const handleDismiss = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDismissing(true);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  if (!announcement || isCollapsed) return <CollapsedHelpButton />;

  return (
    <>
      <AnimatePresence
        onExitComplete={() => {
          if (dismissing) dismiss();
        }}
      >
        {showCard && (
          <motion.div
            key={announcement.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12, transition: { duration: 0.2 } }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              onClick={handleCardClick}
              className="group relative m-px cursor-pointer rounded-lg bg-white duration-150"
            >
              {/* Banner */}
              {announcement.sidebarImage && (
                <div className="flex w-full items-center justify-center p-1.75 pb-0">
                  <img
                    crossOrigin="anonymous"
                    className="overflow-hidden rounded-sm border border-gray-100"
                    src={getAnnouncementImageUrl(announcement.sidebarImage)}
                    alt=""
                  />
                </div>
              )}

              {/* Dismiss button */}
              <button
                onClick={handleDismiss}
                className="absolute -top-1.25 -right-1.25 rounded-full border border-gray-200 bg-white/80 p-0.75 text-gray-500 opacity-0 backdrop-blur-sm duration-150 group-hover:opacity-100 hover:bg-white hover:text-gray-800"
              >
                <X className="size-2.25" />
              </button>

              {/* Text content */}
              <div className="space-y-1 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="mb-px size-1.25 rounded-full bg-teal-400"></div>
                  <p className="text-xs font-medium text-gray-900">{announcement.heading}</p>
                </div>
                <p className="line-clamp-2 text-[11px] text-gray-500">{announcement.preview}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnnouncementModal
        announcement={announcement}
        open={modalOpen}
        onOpenChange={setModalOpen}
        onDismiss={handleDismiss}
      />
    </>
  );
}
